// MQTT carries only an invalidation. Robot data and ownership always come from
// the authenticated API, without running another reconciliation or polling.
export function createRobotReconciliationEvents({
  classRoomCode, isCurrent, getRevision, readState, apply, subscribe, publish, onFailure,
}) {
  let disposed = false;
  let latestRevision = -1;
  let requestedRevision = -1;
  let inFlight = null;
  const current = () => !disposed && isCurrent();

  async function drain() {
    if (!current() || latestRevision <= requestedRevision || latestRevision <= getRevision()) return;
    requestedRevision = latestRevision;
    try {
      const response = await readState();
      if (current()) apply(response.data);
    } catch (failure) {
      onFailure(failure);
    }
  }

  function start() {
    inFlight = Promise.resolve().then(drain).then(() => {
      inFlight = null;
      if (current() && latestRevision > requestedRevision && latestRevision > getRevision()) return start();
      // A failed or rejected read must not consume the notification forever.
      // Reset only after the attempt ends; another delivery can retry, without
      // starting an automatic loop or trusting the notification's revision.
      if (current() && getRevision() < requestedRevision) {
        latestRevision = getRevision();
        requestedRevision = latestRevision;
      }
      return null;
    });
    return inFlight;
  }

  function receive(change) {
    if (!current() || !change || change.classRoom !== classRoomCode ||
        !Number.isSafeInteger(change.revision) || change.revision <= getRevision() ||
        change.revision <= latestRevision) return inFlight;
    latestRevision = change.revision;
    if (!inFlight) return start();
    return inFlight;
  }

  const unsubscribe = subscribe(receive);
  return {
    notify(snapshot) {
      if (!current() || snapshot.classRoom !== classRoomCode || !Number.isSafeInteger(snapshot.revision)) return;
      try {
        publish({ classRoom: classRoomCode, revision: snapshot.revision });
      } catch (failure) {
        onFailure(failure);
      }
    },
    dispose() {
      disposed = true;
      unsubscribe();
    },
  };
}
